/**
 * Hilfsfunktionen für die Formatierung von Daten
 * Zentrale Sammlung für Formatierungslogik
 */

/**
 * Formatiert einen Geldbetrag in deutscher Währungsdarstellung
 */
export const formatCurrency = (
  betrag: number | null | undefined, 
  waehrung: string = 'EUR',
  locale: string = 'de-DE'
): string => {
  // Handle null, undefined, NaN values
  const safeBetrag = (betrag === null || betrag === undefined || isNaN(betrag)) ? 0 : betrag;
  
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: waehrung,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(safeBetrag);
};

/**
 * Formatiert ein Datum in deutscher Darstellung und validiert es
 */
export const formatDate = (
  datum: string | Date,
  locale: string = 'de-DE'
): string => {
  try {
    let date: Date;
    
    if (typeof datum === 'string') {
      // Erkenne deutsches Datumsformat DD.MM.YYYY und konvertiere es
      if (datum.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
        const [day, month, year] = datum.split('.');
        date = new Date(`${year}-${month}-${day}`);
      } else {
        // Verwende Standard-Parsing für ISO-Format
        date = new Date(datum);
      }
    } else {
      date = datum;
    }
    
    // Prüfe auf ungültige Daten
    if (isNaN(date.getTime())) {
      return 'Ungültiges Datum';
    }
    
    // Prüfe auf unrealistische Daten (z.B. vor 1900 oder weit in der Zukunft)
    const year = date.getFullYear();
    if (year < 1900 || year > 2030) {
      return 'Ungültiges Datum';
    }
    
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  } catch {
    return 'Ungültiges Datum';
  }
};

/**
 * Formatiert eine Zahl mit deutschen Tausendertrennern
 */
export const formatNumber = (
  zahl: number,
  nachkommastellen: number = 2,
  locale: string = 'de-DE'
): string => {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: nachkommastellen,
    maximumFractionDigits: nachkommastellen
  }).format(zahl);
};

/**
 * Formatiert einen Prozentsatz
 */
export const formatPercentage = (
  wert: number,
  nachkommastellen: number = 1,
  locale: string = 'de-DE'
): string => {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: nachkommastellen,
    maximumFractionDigits: nachkommastellen
  }).format(wert / 100);
};

/**
 * Kürzt einen Text und fügt Auslassungspunkte hinzu
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

/**
 * Formatiert eine ISIN für bessere Lesbarkeit
 */
export const formatISIN = (isin: string): string => {
  if (!isin || isin.length !== 12) return isin;
  // Format: XX XXXX XXXX XX
  return `${isin.substring(0, 2)} ${isin.substring(2, 6)} ${isin.substring(6, 10)} ${isin.substring(10, 12)}`;
};

/**
 * Formatiert Dateigröße in menschenlesbarer Form
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};